import React, { useState } from "react";
import styled from "styled-components";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  makeStyles,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { useSnackbar } from "notistack";
import useLoader from "../../../hooks/useLoader";
import CaseService from "../../../services/CaseService";
import useUser from "../../../hooks/useUser";
import { getErrorMessage } from "../../../helpers/functions";
import theme from "../../../assets/theme";
import COLORS from "../../../assets/Colors";

const useStyles = makeStyles((theme) => ({
  accordion: {
    borderRadius: "12px !important",
    margin: "16px 0 !important",
    border: "1px solid #e2e8f0",
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
    overflow: "hidden",
    transition: "all 0.2s ease",
    "&:before": { display: "none" },
    "&.Mui-expanded": {
      margin: "20px 0 !important",
      boxShadow: "0 6px 20px rgba(0,131,140,0.14)",
    },
  },
  root: {
    width: "100%",
    backgroundColor: "#f9f9f9",
    border: "solid 0.5px #e1e3ee",
    boxShadow: "none",
    margin: "16px 0px 0px !important",
    "& .MuiIconButton-root": {
      padding: 0,
      marginRight: 15,
      backgroundColor: "#fff",
    },
    "& .MuiSvgIcon-root": {
      fill: "#00838c",
    },
  },
  summary: {
    minHeight: "64px !important",
    padding: "0 24px !important",
    background: "linear-gradient(135deg, #f8fafc, #f1f5f9)",
    borderBottom: "1px solid #e2e8f0",
    "& .MuiAccordionSummary-content": {
      margin: "16px 0 !important",
      alignItems: "center",
      justifyContent: "space-between",
    },
  },
  expandIcon: {
    color: "#00838c",
  },
}));

function TableWithAccordion({
  id,
  title,
  agreementsAndAwards,
  setTriggerCaseCall,
}) {
  const classes = useStyles();
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const { authSetting } = useUser();

  const [expanded, setExpanded] = useState(false);

  const arbitrationTemplates = authSetting?.arbitrationTemplateUrl
    ? Array.isArray(authSetting.arbitrationTemplateUrl)
      ? authSetting.arbitrationTemplateUrl
      : authSetting.arbitrationTemplateUrl.split(",").map((s) => s.trim())
    : [];

  const documents = agreementsAndAwards
    ? Array.isArray(agreementsAndAwards)
      ? agreementsAndAwards
      : agreementsAndAwards.split(",").map((s) => s.trim())
    : arbitrationTemplates;

  const hasDocs = documents.length > 0;

  const refreshAwards = async () => {
    try {
      setLoader({ state: true, message: "Refreshing awards..." });
      const res = await CaseService.refreshAwards(id);
      if (res) {
        enqueueSnackbar("Awards refreshed successfully", {
          variant: "success",
        });
        setTriggerCaseCall(true);
      }
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  };

  return (
    <Accordion
      className={classes.accordion}
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon className={classes.expandIcon} />}
        className={classes.summary}
      >
        <SummaryContent>
          <AccordionHeading>
            {title || "Arbitration Documents"}
          </AccordionHeading>

          <ActionsArea onClick={(e) => e.stopPropagation()}>
            <RefreshLink onClick={refreshAwards}>Refresh Template</RefreshLink>
          </ActionsArea>
        </SummaryContent>
      </AccordionSummary>

      <AccordionDetailsModern>
        {hasDocs ? (
          <DocumentGrid>
            {documents.map((url, idx) => {
              if (!url) return null;

              const fileName = decodeURIComponent(
                url.split("/").pop()?.split("?")[0] || `document-${idx + 1}`,
              );

              return (
                <DocumentCard
                  key={`${url}-${idx}`}
                  onClick={() =>
                    window.open(url, "_blank", "noopener,noreferrer")
                  }
                >
                  <FileIcon>{"📄"}</FileIcon>
                  <FileInfo>
                    <FileName>{fileName}</FileName>
                  </FileInfo>
                  <OpenArrow>↗</OpenArrow>
                </DocumentCard>
              );
            })}
          </DocumentGrid>
        ) : (
          <EmptyState>
            <EmptyText>No documents or templates available yet.</EmptyText>
          </EmptyState>
        )}
      </AccordionDetailsModern>
    </Accordion>
  );
}

export default TableWithAccordion;

const SummaryContent = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

export const AccordionHeading = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 16px;
  color: ${COLORS.COLOR_DARK};
`;

export const ActionsArea = styled.div`
  display: flex;
  align-items: center;
`;

export const RefreshLink = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 14px;
  color: #00838c;
  text-decoration: underline;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    color: #006b75;
    text-decoration: none;
  }
`;

const AccordionDetailsModern = styled(AccordionDetails)`
  padding: 24px !important;
  background: #ffffff;
`;

const DocumentGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const DocumentCard = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.22s ease;

  &:hover {
    background: #f1f5f9;
    transform: translateY(-1px);
    box-shadow: 0 4px 14px rgba(0, 131, 140, 0.1);
  }
`;

const FileIcon = styled.span`
  font-size: 1.6rem;
  color: #64748b;
  flex-shrink: 0;
`;

const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const FileName = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 15px;
  color: #00838c;
  text-decoration: underline;
  word-break: break-all;
`;

const OpenArrow = styled.span`
  font-size: 1.2rem;
  color: #94a3b8;
  opacity: 0.7;
  transition: all 0.2s;

  ${DocumentCard}:hover & {
    opacity: 1;
    color: #00838c;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 32px;
  color: #94a3b8;
  text-align: center;
`;

const EmptyText = styled.div`
  font-size: 1.05rem;
  font-weight: 500;
`;

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 19px 40px;
  margin-top: 15px;
  border-bottom: 1px solid ${COLORS.BORDER_GREY};
`;

export const TableContainer = styled.div`
  width: 100%;
  margin-bottom: 15px;
`;

export const HyperLink = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 14px;
  color: #00838c;
  text-decoration: underline;
  cursor: pointer;
  display: inline-block;
`;

export const AccordionContainer = styled.div`
  padding: 0 20px;
`;

export const ImageWrapper = styled.div`
  cursor: pointer;
`;

export const StyledTitle = styled.div`
  font-family: ${theme.fonts.primaryFontBold};
  width: 200px;
`;
